import { useLayoutEffect, useState } from "react";
import type { AnchorRect } from "../../utils/anchoredPosition";
import { TOUR_ANCHOR_ATTRIBUTE } from "./tourAnchors";
import {
  anchorControlName,
  findAnchorElement,
  prefersReducedMotion,
  scheduleFrame,
} from "./tourDom";

const toRect = (element: HTMLElement | null): AnchorRect | null => {
  if (!element) return null;
  const { top, bottom, left, right, width, height } = element.getBoundingClientRect();
  // A zero rect is an anchor that is hidden or not laid out: show a centered card instead.
  return width > 0 || height > 0 ? { top, bottom, left, right, width, height } : null;
};

const sameRect = (a: AnchorRect | null, b: AnchorRect | null) =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height);

export interface AnchorState {
  /** Where to put the spotlight, or null when there is nothing on screen to spotlight. */
  rect: AnchorRect | null;
  /**
   * The anchor says it has already opened what it opens (`aria-expanded="true"`).
   *
   * Measured here rather than read during render so it cannot disagree with `rect` across a frame:
   * both come from one look at one element, in one layout pass.
   */
  expanded: boolean;
  /**
   * What to call the anchor in the bubble's instruction ("Click **Columns** to continue"), or null
   * when the element gives no short, usable name.
   *
   * Read here for the same reason `expanded` is: one look at one element, in the pass that already
   * has it, rather than the bubble re-querying the page during render and possibly finding a
   * different element from the one the spotlight is on.
   */
  label: string | null;
}

const NOTHING: AnchorState = { rect: null, expanded: false, label: null };

/** A measurement is only ever shown for the step it was taken for. */
interface Measured extends AnchorState {
  stepKey: string;
}

const sameState = (a: AnchorState, b: AnchorState) =>
  a.expanded === b.expanded && a.label === b.label && sameRect(a.rect, b.rect);

/**
 * Live state of the element tagged `anchorId`: where it is, and whether it is already open.
 *
 * Scrolls the anchor into view once per step, the first time it is found, which may be after the
 * step started: an anchor that mounts late is picked up by the observers and scrolled to then. It
 * tracks the anchor through resizes, scrolls anywhere, and DOM changes, re-finding it if it
 * unmounts and comes back. A null rect never advances the tour.
 *
 * The first measure is a layout effect, so a new step never paints with the previous step's
 * spotlight: a measurement is keyed by the step it belongs to, and the new one lands before paint.
 */
export const useAnchorState = (anchorId: string | null, stepKey: string): AnchorState => {
  const [measured, setMeasured] = useState<Measured>({ stepKey, ...NOTHING });

  useLayoutEffect(() => {
    if (!anchorId) return;

    let element: HTMLElement | null = null;
    let scrolled = false;
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => schedule());

    const measure = () => {
      if (!element?.isConnected) {
        const found = findAnchorElement(anchorId);
        if (found !== element) {
          resizeObserver?.disconnect();
          if (found) resizeObserver?.observe(found);
          element = found;
        }
      }
      const next = toRect(element);
      // Spent only on an anchor that has a box: one found inside a collapsed drawer is scrolled to
      // when it is revealed, not wasted while scrollIntoView can do nothing with it.
      if (element && next && !scrolled) {
        scrolled = true;
        if (typeof element.scrollIntoView === "function") {
          element.scrollIntoView({
            block: "center",
            inline: "nearest",
            behavior: prefersReducedMotion() ? "auto" : "smooth",
          });
        }
      }
      const state: AnchorState = {
        rect: next,
        expanded: element?.getAttribute("aria-expanded") === "true",
        label: anchorControlName(element),
      };
      setMeasured((previous) =>
        previous.stepKey === stepKey && sameState(previous, state)
          ? previous
          : { stepKey, ...state }
      );
    };

    let cancelFrame: (() => void) | null = null;
    const schedule = () => {
      if (cancelFrame) return;
      cancelFrame = scheduleFrame(() => {
        cancelFrame = null;
        measure();
      });
    };

    measure();

    /*
      childList catches a modal that mounts (portalled to `body` or rendered in place), which is the
      common case. The attribute filter catches the other one: a panel that is always mounted and
      opens by flipping a class, `inert` or `aria-hidden` (`ManageTeamsPanel`, `ProspectViewerPanel`)
      moves by transform, so nothing is added or removed and the element never changes size — no
      childList record, no ResizeObserver entry, and the step would sit on a stale rect until
      something unrelated redrew the page.

      `aria-expanded` is watched for the other half of this measurement: a trigger that the coach
      opens or closes moves nothing and resizes nothing, and a step that asks for a press it must
      not ask for is the thing the flag exists to prevent.

      `style` is left out on purpose: the bubble repositions itself by inline style, so watching it
      would re-run this measure on the change this measure caused.
    */
    const mutationObserver =
      typeof MutationObserver === "undefined" ? null : new MutationObserver(schedule);
    mutationObserver?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "class",
        "inert",
        "aria-hidden",
        "hidden",
        "aria-expanded",
        TOUR_ANCHOR_ATTRIBUTE,
      ],
    });
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);

    return () => {
      cancelFrame?.();
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [anchorId, stepKey]);

  return anchorId && measured.stepKey === stepKey
    ? { rect: measured.rect, expanded: measured.expanded, label: measured.label }
    : NOTHING;
};
